import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

// Importar ECharts y el módulo GL
import { Scatter3DChart } from 'echarts-gl/charts';
import { Grid3DComponent } from 'echarts-gl/components';
import { TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { WellPath } from 'src/app/interfaces/well';
import { WellService } from 'src/app/services/well.service';

echarts.use([
  Scatter3DChart,
  Grid3DComponent,
  TooltipComponent,
  CanvasRenderer,
]);

@Component({
  selector: 'app-scatter3d-chart',
  standalone: false,
  templateUrl: './scatter3d-chart.component.html',
  styleUrl: './scatter3d-chart.component.scss',
})
export class Scatter3dChartComponent implements AfterViewInit, OnDestroy {
  @Input() wellUwi!: string;
  @Input() readOnlyContent!: boolean;

  surveyData: WellPath = {
    md: [],
    inc: [],
    azi: [],
    tvd: [],
    ew: [],
    ns: [],
    dogleg: [],
    sequence: [],
  };
  selectedIndex: number | null = null;
  darkMode = true;
  isLoading = true;

  @ViewChild('chartContainer', { static: true })
  chartContainer!: ElementRef<HTMLDivElement>;
  private chartInstance: echarts.EChartsType | null = null;
  @ViewChild('tableBody', { static: false })
  tableBody!: ElementRef<HTMLTableSectionElement>;
  @ViewChildren('rowRef') rowRefs!: QueryList<ElementRef<HTMLTableRowElement>>;

  constructor(private wellService: WellService) {}

  ngAfterViewInit(): void {
    this.wellService.getWellPath(this.wellUwi).subscribe((res: WellPath) => {
      this.surveyData = res;
      this.surveyData.tvd = this.surveyData.tvd.map((tvd: number) => -tvd);
      this.isLoading = false;

      this.renderChart();
      window.addEventListener('resize', this.resizeListener);
    });
  }

  private resizeListener = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  onChartClick = (params: any) => {
    if (params && typeof params.dataIndex === 'number') {
      if (this.selectedIndex === params.dataIndex) {
        this.selectedIndex = null;
      } else {
        this.selectedIndex = params.dataIndex;
        setTimeout(() => this.scrollToSelectedRow(), 0);
      }
      this.renderChart();
    }
  };

  onRowClick(i: number) {
    if (this.selectedIndex === i) {
      this.selectedIndex = null;
    } else {
      this.selectedIndex = i;
    }
    this.renderChart();
  }

  getTableIndexes(): number[] {
    return Array.from({ length: this.surveyData.sequence.length }, (_, i) => i);
  }

  renderChart(): void {
    if (!this.chartInstance) {
      this.chartInstance = echarts.init(
        this.chartContainer.nativeElement,
        this.darkMode ? 'dark' : undefined
      );
    } else {
      this.chartInstance.dispose();
      this.chartInstance = echarts.init(
        this.chartContainer.nativeElement,
        this.darkMode ? 'dark' : undefined
      );
    }
    const axisLabelColor = this.darkMode ? '#fff' : '#222';
    const axisLineColor = this.darkMode ? '#aaa' : '#888';
    const option = {
      tooltip: {
        backgroundColor: 'auto',
        borderWidth: 0,
        textStyle: { color: '#fff', fontWeight: 500 },
        formatter: (params: any) => {
          const i = params.dataIndex;
          const dogleg = this.surveyData.dogleg[i];
          const color =
            params.color ||
            (params.data &&
              params.data.itemStyle &&
              params.data.itemStyle.color) ||
            '#222';
          return `<div style="background:${color};padding:6px 10px;min-width:90px;">
            <div style='font-size:13px;font-weight:bold;margin-bottom:2px;'>Sequence ${i}</div>
            <div><b>E/W:</b> ${params.value[0]}</div>
            <div><b>N/S:</b> ${params.value[1]}</div>
            <div><b>TVD:</b> ${params.value[2]}</div>
            <div><b>Dogleg:</b> ${dogleg}</div>
          </div>`;
        },
      },
      xAxis3D: {
        name: 'E/W',
        nameTextStyle: { color: axisLabelColor },
        axisLabel: { color: axisLabelColor },
        axisLine: { lineStyle: { color: axisLineColor } },
      },
      yAxis3D: {
        name: 'N/S',
        nameTextStyle: { color: axisLabelColor },
        axisLabel: { color: axisLabelColor },
        axisLine: { lineStyle: { color: axisLineColor } },
      },
      zAxis3D: {
        name: 'TVD',
        nameTextStyle: { color: axisLabelColor },
        axisLabel: { color: axisLabelColor },
        axisLine: { lineStyle: { color: axisLineColor } },
      },
      grid3D: {
        viewControl: {
          projection: 'perspective',
          distance: 250, // aleja la cámara para hacer zoom out
        },
      },
      series: [
        {
          type: 'scatter3D',
          symbolSize: (value: any, params: any) => {
            if (
              typeof params.dataIndex === 'number' &&
              params.dataIndex === this.selectedIndex
            ) {
              return 20;
            }
            return 12;
          },
          data: this.generateData(),
        },
      ],
    };
    this.chartInstance.setOption(option);
    this.chartInstance.off('click');
    this.chartInstance.on('click', this.onChartClick);
  }

  onDarkModeChange(): void {
    this.darkMode = !this.darkMode;
    this.renderChart();
  }

  scrollToSelectedRow() {
    if (
      this.selectedIndex !== null &&
      this.rowRefs &&
      this.rowRefs.length > this.selectedIndex
    ) {
      const row = this.rowRefs.get(this.selectedIndex)?.nativeElement;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  generateData(): any[] {
    // Usar ew (x), ns (y), tvd (z) y dogleg para color
    const data: any[] = [];
    const len = Math.min(
      this.surveyData.ew.length,
      this.surveyData.ns.length,
      this.surveyData.tvd.length,
      this.surveyData.dogleg.length
    );
    for (let i = 0; i < len; i++) {
      const doglegValue = this.surveyData.dogleg[i];
      let color = '#08306B';
      if (doglegValue < 0.46) {
        color = '#08306B';
      } else if (doglegValue >= 0.46 && doglegValue < 0.93) {
        color = '#4292C6';
      } else if (doglegValue >= 0.93 && doglegValue < 1.4) {
        color = '#66C2A4';
      } else if (doglegValue >= 1.4 && doglegValue < 1.9) {
        color = '#238B45';
      } else if (doglegValue >= 1.9 && doglegValue < 2.3) {
        color = '#FFD700';
      } else if (doglegValue >= 2.3 && doglegValue < 2.8) {
        color = '#FF8C00';
      } else {
        color = '#B22222';
      }
      // Si está seleccionado, poner borde negro
      let itemStyle: any = { color };
      if (this.selectedIndex === i) {
        itemStyle = { ...itemStyle, borderColor: '#222', borderWidth: 3 };
      }
      data.push({
        value: [
          this.surveyData.ew[i], // x
          this.surveyData.ns[i], // y
          this.surveyData.tvd[i], // z
        ],
        itemStyle,
      });
    }
    return data;
  }
}
